/**
 * StatusWorkflowsPage · iter303q — Overwatch Auto-System Admin
 * ─────────────────────────────────────────────────────────────
 * Hierarchy:  STATUS → TASK → PATH → ACTION → SUB-ACTION (2 levels)
 *
 * 3-column layout:
 *   ┌──────────────┬───────────────────────────────┬─────────────┐
 *   │  Statuses    │  Tasks for selected status    │  Registries │
 *   │  (11)        │  ├─ Task card                 │  legend +   │
 *   │  reorder     │  │  ├─ paths tabs (if >1)     │  quick add  │
 *   │  rename      │  │  ├─ prereqs / gate         │  panel      │
 *   │  add/delete  │  │  └─ actions[+subs+subsubs] │             │
 *   └──────────────┴───────────────────────────────┴─────────────┘
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Trash2, ArrowUp, ArrowDown, Loader2, ChevronLeft, ChevronDown,
  ChevronRight, Save, RefreshCw, Lock, GitBranch, Pencil, Check, X, Play, Columns,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ActionRow from './statusWorkflows/ActionRow';

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ────────────────────────────────────────────────────────────────
// Shared axios instance
// ────────────────────────────────────────────────────────────────
const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => { Object.assign(cfg.headers, authHeaders()); return cfg; });

// ────────────────────────────────────────────────────────────────
// Small badge / control helpers
// ────────────────────────────────────────────────────────────────
const Badge = ({ children, tone = 'slate', testid }) => {
  const toneMap = {
    slate:  'bg-slate-100 text-slate-700 border-slate-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue:   'bg-sky-50 text-sky-700 border-sky-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    red:    'bg-rose-50 text-rose-700 border-rose-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span data-testid={testid}
      className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${toneMap[tone]}`}>
      {children}
    </span>
  );
};

const IconBtn = ({ onClick, title, danger, disabled, testid, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testid}
    className={`p-1 rounded transition ${danger ? 'hover:bg-rose-50 text-rose-500' : 'hover:bg-slate-100 text-slate-500'} disabled:opacity-30`}>
    {children}
  </button>
);

// ═════════════════════════════════════════════════════════════════
// Task card — one per row inside the middle column
// ═════════════════════════════════════════════════════════════════
function TaskCard({ task, taskTypes, primitives, onChange, onDelete, onMove, canMoveUp, canMoveDown }) {
  const [expanded, setExpanded] = useState(false);
  const [activePathIdx, setActivePathIdx] = useState(0);
  const currentPath = task.paths?.[activePathIdx];
  const totalActions = (task.paths || []).reduce((s, p) => s + (p.actions?.length || 0), 0);
  const gated = (task.prerequisites?.length || 0) > 0;
  const hasBranching = (task.paths?.length || 0) > 1;
  const testidPrefix = `task-${task.id.slice(0, 8)}`;

  const setField = (patch) => onChange({ ...task, ...patch });

  const setPathField = (idx, patch) => {
    const arr = [...(task.paths || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setField({ paths: arr });
  };

  const addAction = () => {
    const newAction = {
      id: crypto.randomUUID(),
      order: ((currentPath?.actions?.length ?? 0) + 1) * 10,
      type: 'checkbox_tick', params: { label: 'New action' },
      mode: 'auto', sub_actions: [], wait_state: null,
    };
    setPathField(activePathIdx, { actions: [...(currentPath.actions || []), newAction] });
  };

  const setAction = (idx, next) => {
    const arr = [...(currentPath.actions || [])];
    if (next === null) arr.splice(idx, 1);
    else arr[idx] = next;
    setPathField(activePathIdx, { actions: arr });
  };

  const moveAction = (idx, dir) => {
    const arr = [...(currentPath.actions || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setPathField(activePathIdx, { actions: arr });
  };

  const addPath = () => {
    const newPath = {
      id: crypto.randomUUID(), label: `Path ${(task.paths?.length || 0) + 1}`,
      condition: '', actions: [],
    };
    setField({ paths: [...(task.paths || []), newPath] });
    setActivePathIdx((task.paths?.length || 0));
  };

  const deletePath = (idx) => {
    if ((task.paths?.length || 0) <= 1) return;
    const arr = [...task.paths];
    arr.splice(idx, 1);
    setField({ paths: arr });
    setActivePathIdx(0);
  };

  const setPrereq = (val) => {
    if (!val) { setField({ prerequisites: [] }); return; }
    setField({ prerequisites: [{ condition: val }] });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm mb-3" data-testid={testidPrefix}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/60">
        <button onClick={() => setExpanded(v => !v)} data-testid={`${testidPrefix}-expand`}
          className="text-slate-400 hover:text-slate-600">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="text-xs text-slate-400 font-mono w-8">#{task.order}</span>
        <input value={task.label} onChange={e => setField({ label: e.target.value })}
          data-testid={`${testidPrefix}-label`}
          className="flex-1 text-sm font-semibold bg-transparent px-1 py-0.5 rounded hover:bg-white focus:bg-white focus:border-sky-300 border border-transparent" />
        <Badge tone="slate" testid={`${testidPrefix}-type-badge`}>{task.task_type}</Badge>
        <Badge tone={task.mode === 'auto' ? 'blue' : task.mode === 'manual' ? 'amber' : 'purple'}
          testid={`${testidPrefix}-mode-badge`}>{task.mode}</Badge>
        {gated && <Badge tone="red" testid={`${testidPrefix}-gate-badge`}><Lock size={10} className="inline" /> gated</Badge>}
        {hasBranching && <Badge tone="green" testid={`${testidPrefix}-branch-badge`}><GitBranch size={10} className="inline" /> {task.paths.length} paths</Badge>}
        <span className="text-[11px] text-slate-500">{totalActions} action{totalActions !== 1 ? 's' : ''}</span>
        <IconBtn onClick={() => onMove(-1)} disabled={!canMoveUp} title="Move up"
          testid={`${testidPrefix}-up`}><ArrowUp size={14} /></IconBtn>
        <IconBtn onClick={() => onMove(1)} disabled={!canMoveDown} title="Move down"
          testid={`${testidPrefix}-down`}><ArrowDown size={14} /></IconBtn>
        <IconBtn onClick={onDelete} danger title="Delete task"
          testid={`${testidPrefix}-delete`}><Trash2 size={14} /></IconBtn>
      </div>

      {/* Body */}
      {expanded && (
        <div className="p-3 space-y-3">
          {/* Meta row */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Task Type</label>
              <select value={task.task_type} onChange={e => setField({ task_type: e.target.value })}
                data-testid={`${testidPrefix}-type-select`}
                className="w-full text-xs px-2 py-1 border border-slate-300 rounded bg-white">
                {taskTypes.map(tt => <option key={tt.key} value={tt.key}>{tt.group} · {tt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Mode</label>
              <select value={task.mode} onChange={e => setField({ mode: e.target.value })}
                data-testid={`${testidPrefix}-mode-select`}
                className="w-full text-xs px-2 py-1 border border-slate-300 rounded bg-white">
                <option value="auto">auto</option>
                <option value="manual">manual</option>
                <option value="mixed">mixed</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                <input type="checkbox" checked={!!task.blocks_progression}
                  onChange={e => setField({ blocks_progression: e.target.checked })}
                  data-testid={`${testidPrefix}-blocks`} />
                Blocks progression
              </label>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Description</label>
            <textarea value={task.description || ''} onChange={e => setField({ description: e.target.value })}
              data-testid={`${testidPrefix}-description`}
              rows={1}
              className="w-full text-xs px-2 py-1 border border-slate-200 rounded" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <Lock size={10} /> Prerequisite (gate condition — leave blank for ungated)
            </label>
            <input value={task.prerequisites?.[0]?.condition || ''}
              onChange={e => setPrereq(e.target.value)}
              data-testid={`${testidPrefix}-prereq`}
              placeholder='e.g. "deposit_confirmed == true AND operator_tick == true"'
              className="w-full text-xs px-2 py-1 border border-slate-300 rounded font-mono bg-rose-50/40" />
          </div>

          {/* Paths */}
          <div>
            <div className="flex items-center gap-2 border-b border-slate-200 mb-2">
              {(task.paths || []).map((p, idx) => (
                <div key={p.id} className={`flex items-center gap-1 px-2 py-1 cursor-pointer text-xs ${activePathIdx === idx ? 'border-b-2 border-sky-500 text-sky-700 font-semibold' : 'text-slate-500'}`}
                  onClick={() => setActivePathIdx(idx)}
                  data-testid={`${testidPrefix}-path-tab-${idx}`}>
                  {activePathIdx === idx ? (
                    <input value={p.label} onChange={e => setPathField(idx, { label: e.target.value })}
                      data-testid={`${testidPrefix}-path-${idx}-label`}
                      onClick={e => e.stopPropagation()}
                      className="bg-transparent border-b border-sky-300 px-1 min-w-[100px]" />
                  ) : (
                    <span>{p.label}</span>
                  )}
                  {(task.paths.length > 1) && activePathIdx === idx && (
                    <button onClick={(e) => { e.stopPropagation(); deletePath(idx); }}
                      data-testid={`${testidPrefix}-path-${idx}-delete`}
                      className="text-rose-400 hover:text-rose-600 ml-1"><X size={12} /></button>
                  )}
                </div>
              ))}
              <button onClick={addPath} data-testid={`${testidPrefix}-add-path`}
                className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700 flex items-center gap-1">
                <Plus size={11} /> path
              </button>
            </div>
            {/* Path condition input (shown only if >1 path) */}
            {hasBranching && currentPath && (
              <div className="mb-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Path condition</label>
                <input value={currentPath.condition || ''}
                  onChange={e => setPathField(activePathIdx, { condition: e.target.value })}
                  data-testid={`${testidPrefix}-path-${activePathIdx}-condition`}
                  placeholder='e.g. "quote_type == free_estimate"'
                  className="w-full text-xs px-2 py-1 border border-slate-300 rounded font-mono bg-emerald-50/40" />
              </div>
            )}

            {/* Actions list */}
            <div>
              {(currentPath?.actions || []).map((a, idx) => (
                <ActionRow key={a.id} action={a} depth={0} primitives={primitives}
                  onChange={next => setAction(idx, next)}
                  onDelete={() => setAction(idx, null)}
                  onMove={(dir) => moveAction(idx, dir)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < (currentPath.actions.length - 1)}
                />
              ))}
              <button onClick={addAction}
                data-testid={`${testidPrefix}-add-action`}
                className="text-xs px-2 py-1 border border-dashed border-slate-300 rounded w-full text-slate-500 hover:bg-slate-50 hover:text-slate-700 flex items-center justify-center gap-1">
                <Plus size={12} /> Add action
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
// Main page
// ═════════════════════════════════════════════════════════════════
export default function StatusWorkflowsPage() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [primitives, setPrimitives] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [showAddStatus, setShowAddStatus] = useState(false);
  const [editingStatusKey, setEditingStatusKey] = useState(null);

  // ── Initial load ────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [sum, tts, prims] = await Promise.all([
        api.get('/status-workflows/'),
        api.get('/status-workflows/registry/task-types'),
        api.get('/status-workflows/registry/action-primitives'),
      ]);
      setStatuses(sum.data.statuses);
      setTaskTypes(tts.data);
      setPrimitives(prims.data);
      if (!selectedKey && sum.data.statuses.length) {
        setSelectedKey(sum.data.statuses[0].key);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Load failed');
    } finally { setLoading(false); }
  }, [selectedKey]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load workflow when selection changes ────────────────
  const loadWorkflow = useCallback(async (key) => {
    if (!key) return;
    setLoading(true); setError('');
    try {
      const res = await api.get(`/status-workflows/workflow/${key}`);
      setWorkflow(res.data);
      setDirty(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWorkflow(selectedKey); }, [selectedKey, loadWorkflow]);

  // ── Save workflow ────────────────────────────────────────
  const saveWorkflow = async () => {
    if (!workflow || !selectedKey) return;
    setSaving(true); setError('');
    try {
      await api.put(`/status-workflows/workflow/${selectedKey}`, { tasks: workflow.tasks });
      setDirty(false);
      // refresh summary (task counts)
      const sum = await api.get('/status-workflows/');
      setStatuses(sum.data.statuses);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // ── Task ops ─────────────────────────────────────────────
  const setTask = (idx, next) => {
    if (!workflow) return;
    const arr = [...workflow.tasks];
    if (next === null) arr.splice(idx, 1);
    else arr[idx] = next;
    setWorkflow({ ...workflow, tasks: arr });
    setDirty(true);
  };

  const moveTask = (idx, dir) => {
    if (!workflow) return;
    const arr = [...workflow.tasks];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    // reflow order numbers
    arr.forEach((t, i) => t.order = (i + 1) * 10);
    setWorkflow({ ...workflow, tasks: arr });
    setDirty(true);
  };

  const addTask = () => {
    if (!workflow) return;
    const t = {
      id: crypto.randomUUID(),
      order: ((workflow.tasks.length || 0) + 1) * 10,
      label: 'New task', task_type: 'capture_note', mode: 'manual',
      blocks_progression: false, description: '', prerequisites: [],
      paths: [{ id: crypto.randomUUID(), label: 'default', condition: null, actions: [] }],
    };
    setWorkflow({ ...workflow, tasks: [...workflow.tasks, t] });
    setDirty(true);
  };

  // ── Status registry ops ─────────────────────────────────
  const [newStatusKey, setNewStatusKey] = useState('');
  const [newStatusLabel, setNewStatusLabel] = useState('');
  const addStatus = async () => {
    if (!newStatusKey) return;
    try {
      await api.post('/status-workflows/registry/statuses', {
        key: newStatusKey.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        label: newStatusLabel || newStatusKey,
        order: (statuses.length + 1) * 10,
        next_reachable: [],
      });
      setShowAddStatus(false); setNewStatusKey(''); setNewStatusLabel('');
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const deleteStatus = async (key) => {
    if (!window.confirm(`Delete status "${key}"? This removes its workflow too.`)) return;
    try {
      await api.delete(`/status-workflows/registry/statuses/${key}`);
      await loadAll();
      if (selectedKey === key) setSelectedKey(statuses[0]?.key || null);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const [renameLabel, setRenameLabel] = useState('');
  const [renameNext, setRenameNext] = useState('');
  const startEditStatus = (s) => {
    setEditingStatusKey(s.key);
    setRenameLabel(s.label);
    setRenameNext((s.next_reachable || []).join(', '));
  };
  const saveEditStatus = async () => {
    try {
      await api.put(`/status-workflows/registry/statuses/${editingStatusKey}`, {
        label: renameLabel,
        next_reachable: renameNext.split(',').map(s => s.trim()).filter(Boolean),
      });
      setEditingStatusKey(null);
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const moveStatus = async (idx, dir) => {
    const arr = [...statuses];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try {
      await api.post('/status-workflows/registry/statuses/reorder', {
        order: arr.map(s => s.key),
      });
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  // ── Layout ───────────────────────────────────────────────
  const groupedTypes = useMemo(() => {
    const m = {};
    for (const t of taskTypes) { (m[t.group] ||= []).push(t); }
    return m;
  }, [taskTypes]);

  const primitivesByGroup = useMemo(() => {
    const m = {};
    for (const p of primitives) { (m[p.group] ||= []).push(p); }
    return m;
  }, [primitives]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)}
            data-testid="sw-back-btn"
            className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
            <ChevronLeft size={16} /> Back
          </button>
          <h1 className="text-lg font-bold text-slate-800">Status Workflows · Overwatch Auto-System</h1>
          <Badge tone="slate">v2 · 11 statuses · {taskTypes.length} task types · {primitives.length} primitives</Badge>
          <span className="flex-1" />
          <button onClick={() => navigate('/workflow/split')}
            data-testid="sw-split-view-btn"
            className="text-sm px-3 py-1.5 rounded border-2 border-indigo-500 text-indigo-700 font-semibold hover:bg-indigo-50 flex items-center gap-1">
            <Columns size={13} /> Split View
          </button>
          <button onClick={() => navigate('/workflow/start')}
            data-testid="sw-launch-runner-btn"
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700 flex items-center gap-1">
            <Play size={13} /> Launch Runner →
          </button>
          {dirty && <Badge tone="amber">Unsaved changes</Badge>}
          {saving && <span className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Saving…</span>}
          <button onClick={saveWorkflow} disabled={!dirty || saving}
            data-testid="sw-save-btn"
            className="text-sm px-3 py-1.5 rounded bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-40 flex items-center gap-1">
            <Save size={14} /> Save workflow
          </button>
          <button onClick={loadAll} title="Reload"
            data-testid="sw-reload-btn"
            className="text-slate-400 hover:text-slate-700 p-1.5"><RefreshCw size={14} /></button>
        </div>
      </div>

      {error && (
        <div className="max-w-[1800px] mx-auto px-6 py-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded m-4">
          {error}
        </div>
      )}

      <div className="max-w-[1800px] mx-auto grid grid-cols-[260px_1fr_280px] gap-4 p-4">
        {/* ── Left: statuses ────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-lg p-2 h-fit">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-xs uppercase tracking-wide font-semibold text-slate-500">Statuses ({statuses.length})</span>
            <button onClick={() => setShowAddStatus(v => !v)}
              data-testid="sw-add-status-btn"
              className="text-slate-400 hover:text-slate-700"><Plus size={14} /></button>
          </div>
          {showAddStatus && (
            <div className="p-2 bg-slate-50 rounded mb-2 border border-slate-200 space-y-1">
              <input placeholder="key (e.g. quality_review)" value={newStatusKey}
                data-testid="sw-add-status-key"
                onChange={e => setNewStatusKey(e.target.value)}
                className="w-full text-xs px-2 py-1 border border-slate-300 rounded" />
              <input placeholder="Label" value={newStatusLabel}
                data-testid="sw-add-status-label"
                onChange={e => setNewStatusLabel(e.target.value)}
                className="w-full text-xs px-2 py-1 border border-slate-300 rounded" />
              <div className="flex gap-1">
                <button onClick={addStatus}
                  data-testid="sw-add-status-confirm"
                  className="flex-1 text-xs px-2 py-1 bg-sky-600 text-white rounded hover:bg-sky-700">Add</button>
                <button onClick={() => setShowAddStatus(false)}
                  className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700">Cancel</button>
              </div>
            </div>
          )}
          {statuses.map((s, idx) => (
            <div key={s.key} className={`rounded mb-1 ${selectedKey === s.key ? 'bg-sky-50 border-l-4 border-sky-500' : 'hover:bg-slate-50'}`}
              data-testid={`sw-status-row-${s.key}`}>
              {editingStatusKey === s.key ? (
                <div className="p-2 space-y-1">
                  <input value={renameLabel} onChange={e => setRenameLabel(e.target.value)}
                    data-testid={`sw-status-${s.key}-rename-label`}
                    className="w-full text-xs px-2 py-1 border border-slate-300 rounded" />
                  <input value={renameNext} onChange={e => setRenameNext(e.target.value)}
                    placeholder="next_reachable (comma sep)"
                    data-testid={`sw-status-${s.key}-rename-next`}
                    className="w-full text-[10px] px-2 py-1 border border-slate-300 rounded font-mono" />
                  <div className="flex gap-1">
                    <button onClick={saveEditStatus}
                      data-testid={`sw-status-${s.key}-save`}
                      className="flex-1 text-xs px-2 py-1 bg-sky-600 text-white rounded"><Check size={12} className="inline" /></button>
                    <button onClick={() => setEditingStatusKey(null)}
                      className="text-xs px-2 py-1 text-slate-500"><X size={12} /></button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center px-2 py-1.5 gap-1">
                  <button onClick={() => setSelectedKey(s.key)}
                    data-testid={`sw-status-${s.key}-select`}
                    className="flex-1 text-left">
                    <div className="text-sm font-medium text-slate-700">{s.label}</div>
                    <div className="text-[10px] text-slate-400 font-mono flex items-center gap-2">
                      <span>{s.key}</span>
                      <span className="bg-slate-100 rounded px-1">{s.task_count} tasks</span>
                    </div>
                  </button>
                  <IconBtn onClick={() => moveStatus(idx, -1)} disabled={idx === 0} title="Up"
                    testid={`sw-status-${s.key}-up`}><ArrowUp size={12} /></IconBtn>
                  <IconBtn onClick={() => moveStatus(idx, 1)} disabled={idx === statuses.length - 1} title="Down"
                    testid={`sw-status-${s.key}-down`}><ArrowDown size={12} /></IconBtn>
                  <IconBtn onClick={() => startEditStatus(s)} title="Rename / edit"
                    testid={`sw-status-${s.key}-edit`}><Pencil size={12} /></IconBtn>
                  <IconBtn onClick={() => deleteStatus(s.key)} danger title="Delete"
                    testid={`sw-status-${s.key}-delete`}><Trash2 size={12} /></IconBtn>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Middle: tasks ────────────────────── */}
        <div>
          {loading && <div className="text-center py-8 text-slate-400"><Loader2 className="inline animate-spin" /></div>}
          {!loading && workflow && (
            <>
              <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 flex items-center gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Editing</div>
                  <div className="text-lg font-bold text-slate-800">{statuses.find(s => s.key === selectedKey)?.label}</div>
                  <div className="text-xs text-slate-500 font-mono">{selectedKey}</div>
                </div>
                <span className="flex-1" />
                <div className="text-xs text-slate-500">
                  <span className="mr-3">Tasks: <b>{workflow.tasks.length}</b></span>
                  <span>Total actions: <b>{workflow.tasks.reduce((s, t) => s + t.paths.reduce((ss, p) => ss + p.actions.length, 0), 0)}</b></span>
                </div>
              </div>
              {workflow.tasks.map((t, idx) => (
                <TaskCard key={t.id} task={t} taskTypes={taskTypes} primitives={primitives}
                  onChange={next => setTask(idx, next)}
                  onDelete={() => setTask(idx, null)}
                  onMove={dir => moveTask(idx, dir)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < workflow.tasks.length - 1}
                />
              ))}
              <button onClick={addTask} data-testid="sw-add-task-btn"
                className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:bg-white hover:border-sky-400 hover:text-sky-600 flex items-center justify-center gap-2">
                <Plus size={16} /> Add task to {statuses.find(s => s.key === selectedKey)?.label}
              </button>
            </>
          )}
        </div>

        {/* ── Right: registries legend ───────────────────── */}
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Task Types ({taskTypes.length})</div>
            {Object.entries(groupedTypes).map(([grp, list]) => (
              <div key={grp} className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{grp}</div>
                <ul className="pl-2 space-y-0.5">
                  {list.map(t => (
                    <li key={t.key} className="text-[11px] text-slate-600" title={t.description}>
                      • {t.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Action Primitives ({primitives.length})</div>
            {Object.entries(primitivesByGroup).map(([grp, list]) => (
              <div key={grp} className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{grp} ({list.length})</div>
              </div>
            ))}
            <div className="text-[10px] text-slate-400 italic mt-2">
              Nesting: Action → Sub → Sub-sub (2 levels max)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
